import React, { useState } from 'react';
import { ScrollView, View, Alert, TouchableOpacity, Platform } from 'react-native';
import styled from 'styled-components/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useTheme } from '@theme/ThemeProvider';
import { Text, Card, Button, Input, Chip, Modal } from '@ui';
import { useClinics } from '@api/hooks';
import { Clinic } from '@mocks/clinics';

const Container = styled.View`
  flex: 1;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Content = styled(ScrollView)`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.base}px;
`;

const FilterSection = styled.View`
  margin-bottom: ${({ theme }) => theme.spacing.base}px;
`;

const FilterChips = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm}px;
  margin-top: ${({ theme }) => theme.spacing.sm}px;
`;

const ClinicCard = styled(Card)`
  margin-bottom: ${({ theme }) => theme.spacing.md}px;
`;

const ClinicHeader = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing.sm}px;
`;

const RatingContainer = styled.View`
  flex-direction: row;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.xs}px;
`;

const InfoRow = styled.View`
  flex-direction: row;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing.xs}px;
`;

const IconText = styled.View`
  flex-direction: row;
  align-items: center;
  margin-right: ${({ theme }) => theme.spacing.md}px;
`;

const BadgeContainer = styled.View`
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs}px;
  margin-top: ${({ theme }) => theme.spacing.sm}px;
`;

const TimeSlot = styled(TouchableOpacity)<{ selected: boolean }>`
  padding: ${({ theme }) => theme.spacing.md}px;
  border-radius: ${({ theme }) => theme.radius.sm}px;
  border-width: 2px;
  border-color: ${({ theme, selected }) =>
    selected ? theme.colors.brandPrimary : theme.colors.border};
  background-color: ${({ theme, selected }) =>
    selected ? `${theme.colors.brandPrimary}15` : theme.colors.surface};
  margin-bottom: ${({ theme }) => theme.spacing.sm}px;
`;

const timeSlots = [
  { id: '1', time: '9:00 AM', date: 'Tomorrow, Nov 4' },
  { id: '2', time: '11:30 AM', date: 'Tomorrow, Nov 4' },
  { id: '3', time: '2:00 PM', date: 'Nov 5, Tuesday' },
  { id: '4', time: '4:30 PM', date: 'Nov 5, Tuesday' },
  { id: '5', time: '10:00 AM', date: 'Nov 6, Wednesday' },
];

const JourneyClinicsTab: React.FC = () => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState<string | null>(null);
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [bookingDetails, setBookingDetails] = useState<{
    clinicName: string;
    time: string;
    date: string;
  } | null>(null);
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const { data: clinics } = useClinics();

  const districts = Array.from(new Set(clinics?.map(c => c.district) || []));

  const filteredClinics = clinics?.filter(clinic => {
    const matchesSearch =
      searchQuery === '' ||
      clinic.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      clinic.specialty.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesDistrict = !selectedDistrict || clinic.district === selectedDistrict;
    return matchesSearch && matchesDistrict;
  });

  const handleBookAppointment = (clinic: Clinic) => {
    setSelectedClinic(clinic);
    setSelectedTimeSlot(null);
    setBookingModalVisible(true);
  };

  const handleConfirmBooking = () => {
    if (!selectedTimeSlot) {
      if (Platform.OS === 'web') {
        window.alert('Please select a time slot');
      } else {
        Alert.alert('Select Time', 'Please select a time slot');
      }
      return;
    }

    const timeSlot = timeSlots.find(t => t.id === selectedTimeSlot);
    if (!timeSlot || !selectedClinic) return;

    // Store booking details for success modal
    setBookingDetails({
      clinicName: selectedClinic.name,
      time: timeSlot.time,
      date: timeSlot.date,
    });

    setBookingModalVisible(false);

    // Show success modal after a short delay
    setTimeout(() => {
      if (Platform.OS === 'web') {
        // Use custom modal for web
        setSuccessModalVisible(true);
      } else {
        // Use native Alert for mobile
        Alert.alert(
          '✅ Booking Confirmed!',
          `Your appointment at ${selectedClinic.name} has been booked for ${timeSlot.time} on ${timeSlot.date}.`,
          [{ text: 'OK' }]
        );
      }
    }, 300);
  };

  return (
    <Container>
      <Content showsVerticalScrollIndicator={false}>
        <FilterSection>
          <Input
            placeholder="Search clinics..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            leftIcon="search"
            rightIcon={searchQuery ? 'close' : undefined}
            onRightIconPress={() => setSearchQuery('')}
          />
          <FilterChips>
            <Chip
              variant={selectedDistrict === null ? 'filled' : 'outlined'}
              size="sm"
              onPress={() => setSelectedDistrict(null)}
            >
              All Districts
            </Chip>
            {districts.map(district => (
              <Chip
                key={district}
                variant={selectedDistrict === district ? 'filled' : 'outlined'}
                size="sm"
                onPress={() => setSelectedDistrict(district)}
              >
                {district}
              </Chip>
            ))}
          </FilterChips>
        </FilterSection>

        {filteredClinics?.map(clinic => (
          <ClinicCard key={clinic.id} padding="md">
            <ClinicHeader>
              <View style={{ flex: 1 }}>
                <Text variant="titleMedium" color="textPrimary">
                  {clinic.name}
                </Text>
                <Text variant="bodySmall" color="textSecondary" style={{ marginTop: 2 }}>
                  {clinic.specialty}
                </Text>
                <RatingContainer>
                  <Icon name="star" size={16} color={theme.colors.warning} />
                  <Text variant="bodySmall" color="textPrimary" style={{ marginLeft: 4 }}>
                    {clinic.rating}
                  </Text>
                </RatingContainer>
              </View>
            </ClinicHeader>

            <InfoRow>
              <IconText>
                <Icon name="location-on" size={16} color={theme.colors.textSecondary} />
                <Text variant="bodySmall" color="textSecondary" style={{ marginLeft: 4 }}>
                  {clinic.distance}
                </Text>
              </IconText>
              <IconText>
                <Icon name="phone" size={16} color={theme.colors.textSecondary} />
                <Text variant="bodySmall" color="textSecondary" style={{ marginLeft: 4 }}>
                  {clinic.phone}
                </Text>
              </IconText>
            </InfoRow>

            <Text variant="bodySmall" color="textSecondary" style={{ marginTop: 4 }}>
              {clinic.address}
            </Text>

            <BadgeContainer>
              {clinic.networkProvider && (
                <Chip variant="tonal" size="sm" icon="verified">
                  Network Provider
                </Chip>
              )}
              {clinic.acceptsNewPatients && (
                <Chip variant="tonal" size="sm" icon="check-circle">
                  Accepting New Patients
                </Chip>
              )}
            </BadgeContainer>

            <Button
              variant="primary"
              fullWidth
              onPress={() => handleBookAppointment(clinic)}
              style={{ marginTop: 12 }}
              iconRight="arrow-forward"
            >
              Book Appointment
            </Button>
          </ClinicCard>
        ))}

        {filteredClinics?.length === 0 && (
          <Card padding="lg">
            <Text variant="body" color="textSecondary" align="center">
              No clinics found matching your criteria
            </Text>
          </Card>
        )}

        <View style={{ height: 24 }} />
      </Content>

      {/* Booking Modal */}
      <Modal
        visible={bookingModalVisible}
        onClose={() => setBookingModalVisible(false)}
        title="Book Appointment"
      >
        <ScrollView
          style={{ flexGrow: 1 }}
          contentContainerStyle={{
            flexGrow: 1,
            paddingBottom: 16,
          }}
          showsVerticalScrollIndicator={false}
        >
          {selectedClinic && (
            <>
              <Card padding="md" variant="outlined" style={{ marginBottom: 16 }}>
                <Text variant="titleMedium" color="textPrimary">
                  {selectedClinic.name}
                </Text>
                <Text variant="bodySmall" color="textSecondary" style={{ marginTop: 4 }}>
                  {selectedClinic.specialty}
                </Text>
                <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
                  <Icon name="location-on" size={14} color={theme.colors.textSecondary} />
                  <Text variant="bodySmall" color="textSecondary" style={{ marginLeft: 4 }}>
                    {selectedClinic.address}
                  </Text>
                </View>
              </Card>

              <Text variant="titleMedium" color="textPrimary" style={{ marginBottom: 12 }}>
                Select Time Slot
              </Text>

              {timeSlots.map(slot => (
                <TimeSlot
                  key={slot.id}
                  selected={selectedTimeSlot === slot.id}
                  onPress={() => setSelectedTimeSlot(slot.id)}
                >
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Text
                      variant="body"
                      color={selectedTimeSlot === slot.id ? 'brandPrimary' : 'textPrimary'}
                    >
                      {slot.time}
                    </Text>
                    <Text
                      variant="bodyMedium"
                      color={selectedTimeSlot === slot.id ? 'brandPrimary' : 'textSecondary'}
                    >
                      {slot.date}
                    </Text>
                  </View>
                </TimeSlot>
              ))}

              <Button
                variant="primary"
                fullWidth
                onPress={handleConfirmBooking}
                disabled={!selectedTimeSlot}
                style={{ marginTop: 16 }}
              >
                Confirm Booking
              </Button>
              <Button
                variant="ghost"
                fullWidth
                onPress={() => setBookingModalVisible(false)}
                style={{ marginTop: 8 }}
              >
                Cancel
              </Button>
            </>
          )}
        </ScrollView>
      </Modal>

      {/* Success Modal */}
      <Modal visible={successModalVisible} onClose={() => setSuccessModalVisible(false)}>
        <View style={{ alignItems: 'center', paddingVertical: 24 }}>
          <View
            style={{
              width: 64,
              height: 64,
              borderRadius: 32,
              backgroundColor: theme.colors.successLight,
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 16,
            }}
          >
            <Icon name="check-circle" size={40} color={theme.colors.success} />
          </View>
          <Text variant="title" color="textPrimary" align="center" style={{ marginBottom: 8 }}>
            ✅ Booking Confirmed!
          </Text>
          {bookingDetails && (
            <>
              <Text variant="body" color="textSecondary" align="center" style={{ marginBottom: 4 }}>
                Your appointment at {bookingDetails.clinicName} has been booked for
              </Text>
              <Text
                variant="bodyMedium"
                color="textPrimary"
                align="center"
                style={{ marginBottom: 16 }}
              >
                {bookingDetails.time} on {bookingDetails.date}
              </Text>
            </>
          )}
          <Button
            variant="primary"
            fullWidth
            onPress={() => setSuccessModalVisible(false)}
            style={{ marginTop: 8 }}
          >
            OK
          </Button>
        </View>
      </Modal>
    </Container>
  );
};

export default JourneyClinicsTab;
